// src/components/PrivacyPolicy.jsx
import React from "react";
import { Link } from "react-router-dom";
import {
  FiShield,
  FiEye,
  FiLock,
  FiUsers,
  FiGlobe,
  FiMail,
} from "react-icons/fi";

const PrivacyPolicy = () => {
  const lastUpdated = "December 15, 2024";

  const sections = [
    {
      id: "information-we-collect",
      title: "Information We Collect",
      icon: FiEye,
      content: [
        {
          subtitle: "Personal Information",
          details: [
            "Name, email address, phone number, and mailing address",
            "Date of birth and nationality",
            "Educational background and academic records",
            "Test scores (SAT, TOEFL, IELTS, etc.)",
            "Financial information for scholarship and funding applications",
            "Passport and visa documentation",
            "Parent/guardian contact information (for minors)",
          ],
        },
        {
          subtitle: "Automatically Collected Information",
          details: [
            "IP address and device identifiers",
            "Browser type and version",
            "Operating system information",
            "Pages visited and time spent on our platform",
            "Referral sources and exit pages",
            "Cookie and similar technology data",
          ],
        },
        {
          subtitle: "Educational Services Data",
          details: [
            "University and course preferences",
            "Application materials and essays",
            "Counselor session notes and recommendations",
            "Progress tracking and milestone data",
            "Communication records with universities",
            "Visa application status and documentation",
          ],
        },
      ],
    },
    {
      id: "how-we-use-information",
      title: "How We Use Your Information",
      icon: FiUsers,
      content: [
        {
          subtitle: "Educational Services",
          details: [
            "Provide personalized educational counseling and guidance",
            "Assist with university applications and admissions processes",
            "Facilitate scholarship and financial aid applications",
            "Coordinate with educational institutions on your behalf",
            "Provide test preparation and academic support services",
            "Track your application progress and provide updates",
          ],
        },
        {
          subtitle: "Platform Operations",
          details: [
            "Create and maintain your user account",
            "Process payments and billing for services",
            "Send important service notifications and updates",
            "Provide customer support and respond to inquiries",
            "Improve our platform functionality and user experience",
            "Conduct analytics to enhance our services",
          ],
        },
        {
          subtitle: "Legal and Safety",
          details: [
            "Comply with legal obligations and regulations",
            "Protect against fraud and unauthorized access",
            "Enforce our terms of service and policies",
            "Respond to legal requests and court orders",
            "Ensure platform security and data integrity",
          ],
        },
      ],
    },
    {
      id: "information-sharing",
      title: "Information Sharing and Disclosure",
      icon: FiGlobe,
      content: [
        {
          subtitle: "Educational Partners",
          details: [
            "Universities and colleges for application purposes",
            "Scholarship organizations and funding bodies",
            "Test preparation providers and examination boards",
            "Visa application centers and embassy services",
            "Academic credential evaluation services",
          ],
        },
        {
          subtitle: "Service Providers",
          details: [
            "Cloud hosting and data storage providers",
            "Payment processing companies",
            "Email and communication service providers",
            "Analytics and performance monitoring tools",
            "Customer support and chat services",
          ],
        },
        {
          subtitle: "Legal Requirements",
          details: [
            "Government agencies when required by law",
            "Court orders and legal proceedings",
            "Protection of rights, property, or safety",
            "Prevention of fraud or illegal activities",
            "Compliance with regulatory requirements",
          ],
        },
      ],
    },
    {
      id: "data-security",
      title: "Data Security and Protection",
      icon: FiLock,
      content: [
        {
          subtitle: "Technical Safeguards",
          details: [
            "256-bit SSL encryption for data transmission",
            "Advanced encryption for data storage",
            "Multi-factor authentication requirements",
            "Regular security audits and penetration testing",
            "Secure cloud infrastructure with redundancy",
            "Real-time monitoring and threat detection",
          ],
        },
        {
          subtitle: "Access Controls",
          details: [
            "Role-based access permissions for staff",
            "Regular access reviews and updates",
            "Secure authentication protocols",
            "Limited data access on need-to-know basis",
            "Audit logs for all data access activities",
          ],
        },
        {
          subtitle: "Data Backup and Recovery",
          details: [
            "Automated daily backups of all data",
            "Geographically distributed backup storage",
            "Tested disaster recovery procedures",
            "Business continuity planning",
            "Regular backup integrity verification",
          ],
        },
      ],
    },
    {
      id: "your-rights",
      title: "Your Privacy Rights",
      icon: FiShield,
      content: [
        {
          subtitle: "Access and Control",
          details: [
            "Right to access your personal information",
            "Right to correct inaccurate or incomplete data",
            "Right to delete your personal information",
            "Right to restrict or object to processing",
            "Right to data portability and export",
            "Right to withdraw consent where applicable",
          ],
        },
        {
          subtitle: "Communication Preferences",
          details: [
            "Opt-out of marketing communications",
            "Choose notification preferences",
            "Control cookie and tracking settings",
            "Manage third-party data sharing",
            "Set privacy preferences for your account",
          ],
        },
        {
          subtitle: "Geographic Rights",
          details: [
            "GDPR rights for European Union residents",
            "CCPA rights for California residents",
            "Local privacy law protections where applicable",
            "Right to lodge complaints with supervisory authorities",
          ],
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 pt-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-xl">
              <FiShield size={32} className="text-white" />
            </div>
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 bg-clip-text text-transparent mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Your privacy is fundamental to our mission. This policy explains how
            EduConsultancy collects, uses, protects, and shares your personal
            information.
          </p>
          <div className="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400">
            <span>Last updated:</span>
            <time className="font-semibold">{lastUpdated}</time>
          </div>
        </div>

        {/* Overview */}
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-4">
            Privacy Overview
          </h2>
          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              EduConsultancy is committed to protecting your privacy and
              ensuring the secure handling of your personal information. This
              Privacy Policy describes how we collect, use, disclose, and
              safeguard your information when you use our educational
              consultancy services, website, and mobile applications.
            </p>
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed mt-4">
              By using our services, you consent to the collection and use of
              your information as described in this policy. We encourage you to
              read this policy carefully and contact us if you have any
              questions.
            </p>
          </div>
        </div>

        {/* Main Content Sections */}
        {sections.map((section, index) => (
          <div
            key={section.id}
            className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <section.icon size={24} className="text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
                {index + 1}. {section.title}
              </h2>
            </div>

            <div className="space-y-6">
              {section.content.map((item, itemIndex) => (
                <div key={itemIndex}>
                  <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-300 mb-3">
                    {item.subtitle}
                  </h3>
                  <ul className="space-y-2">
                    {item.details.map((detail, detailIndex) => (
                      <li
                        key={detailIndex}
                        className="flex items-start gap-3 text-slate-600 dark:text-slate-400"
                      >
                        <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                        <span className="leading-relaxed">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Additional Important Sections */}
        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            6. Cookies and Tracking Technologies
          </h2>
          <div className="space-y-4">
            <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
              We use cookies and similar tracking technologies to enhance your
              experience on our platform. These technologies help us remember
              your preferences, analyze site traffic, and provide personalized
              content.
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Essential Cookies
                </h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  Required for basic platform functionality, security, and user
                  authentication.
                </p>
              </div>
              <div>
                <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                  Analytics Cookies
                </h4>
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  Help us understand how users interact with our platform to
                  improve services.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            7. International Data Transfers
          </h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            As we serve students globally, your information may be transferred
            to and processed in countries other than your residence. We ensure
            appropriate safeguards are in place for international transfers,
            including:
          </p>
          <ul className="space-y-2">
            {[
              "Standard Contractual Clauses (SCCs) for EU data transfers",
              "Adequacy decisions where available",
              "Appropriate technical and organizational measures",
              "Regular assessments of transfer mechanisms",
            ].map((item, index) => (
              <li
                key={index}
                className="flex items-start gap-3 text-slate-600 dark:text-slate-400"
              >
                <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-2xl p-8 mb-8 border border-slate-200/50 dark:border-slate-700/50 shadow-xl">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200 mb-6">
            8. Data Retention
          </h2>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
            We retain your personal information only as long as necessary to
            fulfill the purposes outlined in this policy, unless a longer
            retention period is required or permitted by law:
          </p>
          <div className="grid gap-4">
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Active Accounts
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                Data retained while account is active and for reasonable period
                after to assist with future services.
              </p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Application Records
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                Educational records retained for 7 years or as required by
                educational institutions.
              </p>
            </div>
            <div className="p-4 bg-slate-50 dark:bg-slate-700 rounded-xl">
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Financial Data
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                Payment information retained for tax and accounting purposes as
                required by law.
              </p>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gradient-to-r from-blue-500/10 to-purple-500/10 dark:from-blue-400/10 dark:to-purple-400/10 rounded-2xl p-8 border border-blue-200/50 dark:border-blue-700/50">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <FiMail size={24} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
              Contact Us About Privacy
            </h2>
          </div>
          <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-6">
            If you have questions about this Privacy Policy, want to exercise
            your privacy rights, or need to report a privacy concern, please
            contact us:
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Privacy Officer
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-1">
                Email: privacy@educonsultancy.com
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                Phone: +977 9876543210
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                Mailing Address
              </h4>
              <p className="text-slate-600 dark:text-slate-400 text-sm">
                EduConsultancy Privacy Department
                <br />
                Baisepati
                <br />
                Lalitpur
                <br />
                Nepal
              </p>
            </div>
          </div>
          <div className="mt-6 p-4 bg-white/50 dark:bg-slate-800/50 rounded-xl">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              <strong>Response Time:</strong> We will respond to privacy
              inquiries within 30 days of receipt. For urgent privacy matters,
              please call our privacy hotline for immediate assistance.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
